import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { PolicyFitStatus } from '@ballerine/common';

import { BookText, ExternalLink } from '../../../../common/icons/lucide-adapter';
import { markdownComponents } from '../../report-shared/components/markdown-components';
import { PolicyFitIcon } from '../../report-shared/components/PolicyFitBadge/PolicyFitBadge';
import { ReportWrapper } from '../../report-shared/components/ReportWrapper';

type YourPolicyFitSectionProps = {
  policyFit?: {
    status?: PolicyFitStatus;
    description?: string | null;
    displayName?: string | null;
    version?: number | null;
    originalPolicyFileId?: string | null;
  } | null;
  policy?: {
    name?: string | null;
  } | null;
  onOpenOriginalPolicy?: () => void;
};

export const YourPolicyFitSection: React.FC<YourPolicyFitSectionProps> = ({
  policyFit,
  policy,
  onOpenOriginalPolicy,
}) => {
  if (!policyFit) {
    return null;
  }

  const showOriginalPolicyLink = policyFit.originalPolicyFileId && onOpenOriginalPolicy;
  // Legacy reports (created before aup_policy_name was persisted) have no policy.name;
  // suppress the "Policy X v.N" badge in that case rather than falling back to the
  // customer's display name — for multi-policy customers that would be actively misleading.
  const policyDisplayName = policy?.name ?? null;

  return (
    <ReportWrapper showTooltip={false}>
      <div className="flex flex-col gap-4 pt-10">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold text-slate-800">Your Policy Fit</h2>
          <div className="flex items-center gap-4">
            {policyDisplayName && policyFit.version != null && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <BookText className="h-4 w-4" />
                Policy {policyDisplayName} v.{policyFit.version}
                {showOriginalPolicyLink && (
                  <button
                    type="button"
                    onClick={onOpenOriginalPolicy}
                    className="ml-1 inline-flex items-center text-purple-600 hover:text-purple-700"
                    title="View original policy document"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            )}
            <PolicyFitIcon policyFit={policyFit} variant="simple" withStatusText={true} />
          </div>
        </div>

        {policyFit.description && (
          <div className="prose prose-sm max-w-none text-sm leading-snug">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {policyFit.description}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </ReportWrapper>
  );
};
